import React, { use, useCallback, useMemo } from "react";
import { useI18n } from "../../hooks/i18n-hooks";
import VStack from "../../ui_atoms/VStack";
import { useIntercom } from "../../hooks/intercom";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { useDeleteCurrentProfile } from "../../hooks/use-delete-current-profile";
import { ClientProfile, ProfileType } from "../../__generated__/graphql";
import { useDeleteAccount } from "../../hooks/use-delete-account";
import ScreenCard, { ButtonType } from "../ScreenCard";
import { kycGradient } from "../../configs/linear-gradients";
import { LiftPerson } from "../../components/LiftPerson";
import { escapeMarkdown } from "../../utils";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const VerificationFailed = () => {
    const i18n = useI18n();
    const insets = useSafeAreaInsets();
    const { openProfileSwitch, profileIds } = use(ProfileSwitcherManagerContext);
    const onDeleteClientProfile = useDeleteCurrentProfile({ profileType: ProfileType.Client });
    const onDeleteAccount = useDeleteAccount();

    const currentProfile = useCurrentProfile<ClientProfile>();

    const reason = currentProfile?.client?.rejectedReason;

    const hasMultipleProfiles = useMemo(() => profileIds.length > 1, [profileIds.length]);

    const { presentArticleId, presentIntercom } = useIntercom();

    const onAssistance = useCallback(() => {
        void presentIntercom();
    }, [presentIntercom]);

    const openMarkdownLink = useCallback(
        (url: string) => {
            void (async () => {
                if (url.startsWith("intercom://")) {
                    const articleId = url.split("intercom://")[1];
                    await presentArticleId(articleId);
                }
            })();
            return false;
        },
        [presentArticleId],
    );

    const buttons = useMemo<ButtonType[]>(
        () =>
            hasMultipleProfiles
                ? [
                      {
                          title: i18n.t(`screens.auth.verificationFailed.delete_profile`),
                          onPress: onDeleteClientProfile,
                          mode: "danger",
                      },
                      {
                          title: i18n.t("profile.change_profile"),
                          onPress: openProfileSwitch,
                          mode: "secondary",
                      },
                  ]
                : [
                      {
                          title: i18n.t(`screens.auth.verificationFailed.delete_account`),
                          onPress: onDeleteAccount,
                          mode: "danger",
                      },
                      {
                          title: i18n.t("assistance"),
                          onPress: onAssistance,
                          mode: "secondary",
                      },
                  ],
        [
            hasMultipleProfiles,
            i18n,
            onAssistance,
            onDeleteAccount,
            onDeleteClientProfile,
            openProfileSwitch,
        ],
    );

    return (
        <ScreenCard
            backgroundGradient={kycGradient}
            buttons={buttons}
            contentStyle={{
                paddingTop: insets.top,
                paddingBottom: 0,
            }}
            scrollEnabled={false}
            isContentContainerFlex
        >
            <VStack v={1} h={1} style={{ flex: 1 }}>
                <LiftPerson
                    markdownProps={{
                        markdown: `${i18n.t("screens.auth.verificationFailed.description")}${reason ? `\n\n${escapeMarkdown(reason)}` : ""}`,
                        onLinkPress: openMarkdownLink,
                    }}
                />
            </VStack>
        </ScreenCard>
    );
};

export default VerificationFailed;
